/**
 * Homepage Reviews Hook - React Query data fetching
 *
 * Separate query + separate cache from homepageConfig.
 * Returns the latest approved reviews as transformed cards.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { STATIC_DATA_STALE_TIME, STATIC_DATA_GC_TIME } from '../../constants'
import { homepageApi } from '../../lib/apiClient'
import { transformReviewsToCards } from '../../lib/formatters'
import { homepageKeys } from '../queryKeys'

import type { ReviewCard } from '../../types'

export interface UseHomepageReviewsResult {
  /** Transformed review cards (camelCase) */
  reviews: ReviewCard[]
  /** Loading state */
  isLoading: boolean
}

/**
 * Fetches homepage reviews from a dedicated query (separate from homepageConfig).
 *
 * Returns the latest approved reviews as transformed cards. The section is
 * shown whenever there are reviews; there is no admin gating.
 */
export function useHomepageReviews(): UseHomepageReviewsResult {
  const query = useQuery({
    queryKey: homepageKeys.reviews(),
    queryFn: () => homepageApi.getReviews(),
    staleTime: STATIC_DATA_STALE_TIME,
    gcTime: STATIC_DATA_GC_TIME,
  })

  const data = query.data?.homepageReviews ?? null

  const reviews = transformReviewsToCards(data?.items ?? [])

  const error = query.error instanceof Error
    ? query.error
    : query.error
      ? new Error(String(query.error))
      : null

  // De-silence the reviews query: a failed fetch otherwise looks identical to
  // "no reviews" (empty list → section hidden). Log it so the failure is
  // visible in client logging instead of vanishing. (TSS-535 review)
  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console -- intentional: surfaces a silent reviews-fetch failure in client logging (TSS-535)
      console.error('[homepageReviews] query failed; reviews section hidden', error)
    }
  }, [error])

  return {
    reviews,
    isLoading: query.isLoading,
  }
}
